import React, { useCallback, useMemo, Suspense, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CancelCrossIcon, Sheet } from "@activecollab/components";
import PropTypes from "prop-types";
import ErrorBoundary from "~/components/ErrorBoundry";
import { useGoogleDriveListener } from "~/components/Google/useGoogleDriveListener";
import SectionLoader from "~/components/SectionLoader/SectionLoader";
import { useObjectSheetContext } from "~/components/Sheet/ObjectSheetContext";
import { ProjectTemplateContextProvider } from "~/pages/ProjectTemplates/context";
import { TemplateTask } from "~/pages/ProjectTemplates/Tasks/TemplateTask";
import { TemplateTaskEditForm } from "~/pages/ProjectTemplates/Tasks/TemplateTaskEditForm";
import { fetchProjectTemplateElements } from "~/store/actions/actions.projectTemplateElements";
import { fetchTemplate } from "~/store/actions/actions.projectTemplates";
import { getProjectTemplateElementById } from "~/store/selectors/selector.projectTemplateElements";
import { getProjectTemplateById } from "~/store/selectors/selector.projectTemplates";
import NotFoundSlate from "../EmptySlate/NotFoundSlate";
import { Resolver } from "../Resolver";

export const TemplateTaskSheet = React.forwardRef(({ taskId, templateId, onClose, open }, ref) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showTaskForm, setShowTaskForm] = useState(location?.state?.edit || false);

  const task = useSelector((state) => getProjectTemplateElementById(state, taskId));
  const template = useSelector((state) => getProjectTemplateById(state, templateId));

  useEffect(() => {
    setShowTaskForm(location?.state?.edit || false);
  }, [location?.state?.edit]);

  const { closeSheet } = useObjectSheetContext();
  const googlePicker = useGoogleDriveListener();

  const resolveDependencies = useCallback(async () => {
    await dispatch(fetchTemplate(templateId)).then(({ error }) => {
      if (error) {
        throw error;
      }
    });
    await dispatch(fetchProjectTemplateElements(templateId));
  }, [dispatch, templateId]);

  const handleCloseEditTaskForm = useCallback(() => {
    navigate(location.pathname + location.search, {
      replace: true,
      state: { ...(location.state || {}), edit: false },
    });
    setShowTaskForm(false);
  }, [location.pathname, location.search, location.state, navigate]);

  const handleClose = useCallback(() => {
    if (typeof onClose === "function") onClose();
    if (showTaskForm) handleCloseEditTaskForm();
  }, [handleCloseEditTaskForm, onClose, showTaskForm]);

  const controls = useMemo(
    () => [
      {
        tooltip: t("Close"),
        onClick: closeSheet,
        icon: <CancelCrossIcon />,
        className: "task_modal_close_icon",
      },
    ],
    [closeSheet, t]
  );

  return (
    <Sheet
      ref={ref}
      open={open}
      position="right"
      animation="right"
      mode="stretch"
      onClose={handleClose}
      controls={controls}
      className="task-modal-container"
      disableFocusLock={googlePicker}
    >
      <Suspense fallback={<SectionLoader />}>
        <ErrorBoundary errorLocation="TemplateTaskSheet">
          <Resolver
            callback={resolveDependencies}
            loadingComponent={<SectionLoader />}
            errorComponent={
              <NotFoundSlate
                header={t("Project Template Task not found")}
                pageName={t("Project Template Task")}
                pageLink={`/projects/templates/${templateId}`}
              />
            }
            update
          >
            <ProjectTemplateContextProvider value={{ templateId: +templateId, template }}>
              {!task ? (
                <NotFoundSlate
                  header={t("Project Template Task not found")}
                  pageName={t("Project Template Task")}
                  pageLink={`/projects/templates/${templateId}`}
                />
              ) : !showTaskForm ? (
                <TemplateTask
                  templateId={templateId}
                  taskId={taskId}
                />
              ) : (
                <TemplateTaskEditForm
                  templateId={templateId}
                  taskId={taskId}
                />
              )}
            </ProjectTemplateContextProvider>
          </Resolver>
        </ErrorBoundary>
      </Suspense>
    </Sheet>
  );
});

TemplateTaskSheet.propTypes = {
  taskId: PropTypes.number,
  templateId: PropTypes.number,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

TemplateTaskSheet.defaultProps = {
  taskId: 0,
  templateId: 0,
  open: false,
};

TemplateTaskSheet.displayName = "TemplateTaskSheet";
